import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCandidateOnboardingStatus, useCandidateTeamSnapshot } from '../../hooks/useCandidateProfile';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
  requireTeamCandidate?: boolean;
}

type LocationState = {
  returnTo?: string;
};

export function ProtectedRoute({ children, requiredRole, requireTeamCandidate = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, login, error, user } = useAuth();
  const location = useLocation();
  const loginRequestedRef = useRef(false);
  const [candidateRouteLoadingTimeout, setCandidateRouteLoadingTimeout] = useState(false);

  const isCandidateRoute = location.pathname.startsWith('/candidate/');
  const isCandidateOnboardingRoute = location.pathname.startsWith('/candidate/onboarding');
  const requireTeamGate = requireTeamCandidate && isCandidateRoute && !isCandidateOnboardingRoute;
  const {
    data: onboardingStatus,
    isLoading: onboardingLoading,
    error: onboardingError,
    refetch: refetchOnboardingStatus,
  } = useCandidateOnboardingStatus(isCandidateRoute);
  const {
    data: teamSnapshot,
    isLoading: teamLoading,
    isFetching: teamFetching,
  } = useCandidateTeamSnapshot(requireTeamGate && Boolean(onboardingStatus?.onboardingCompleted));

  const roles = (user as { [key: string]: unknown })?.['https://lighthouse.jobs/roles'] as string[] | undefined;
  const hasRole = requiredRole ? roles?.includes(requiredRole) : true;

  const returnPath = useMemo(() => {
    if (!location.pathname) return '/';
    const suffix = `${location.search ?? ''}${location.hash ?? ''}`;
    return `${location.pathname}${suffix}`;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (isLoading || isAuthenticated || loginRequestedRef.current) return;
    loginRequestedRef.current = true;
    void login({ appState: { returnTo: location.pathname } });
  }, [isAuthenticated, isLoading, location.pathname, login]);

  useEffect(() => {
    if (!isCandidateRoute || !onboardingLoading) {
      setCandidateRouteLoadingTimeout(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCandidateRouteLoadingTimeout(true);
    }, 15000);
    return () => window.clearTimeout(timeoutId);
  }, [isCandidateRoute, onboardingLoading, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">Loading secure area...</div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">
        Redirecting to secure login... {error && <span className="ml-2 text-red-400">{error.message}</span>}
      </div>
    );
  }

  if (!hasRole) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-gray-300">
        You do not have access to this area. Contact the Lighthouse team to request the appropriate permissions.
      </div>
    );
  }

  if (isCandidateRoute) {
    if (isCandidateOnboardingRoute) {
      if (onboardingLoading) {
        return (
          <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">
            Preparing your onboarding experience...
          </div>
        );
      }

      if (onboardingError) {
        return <>{children}</>;
      }

      if (onboardingStatus?.onboardingCompleted) {
        const state = location.state as LocationState | null;
        const target = state?.returnTo ?? '/candidate/dashboard';
        return <Navigate to={target} replace />;
      }

      return <>{children}</>;
    }

    if (onboardingLoading) {
      if (candidateRouteLoadingTimeout) {
        return (
          <div className="mx-auto flex min-h-[60vh] w-full max-w-xl flex-col items-center justify-center gap-4 px-4 text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <p className="text-sm text-gray-300">Candidate dashboard is taking longer than expected to initialize.</p>
              <p className="mt-2 text-xs text-gray-400">
                This usually means your auth token could not be retrieved. Use the actions below to recover.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    window.location.reload();
                  }}
                  className="rounded-lg border border-white/20 px-3 py-1.5 text-xs text-white hover:bg-white/10"
                >
                  Reload page
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = '/candidate';
                  }}
                  className="rounded-lg border border-[#d2ac47]/60 bg-[#ba9036]/20 px-3 py-1.5 text-xs text-[#f3d98a] hover:bg-[#ba9036]/30"
                >
                  Re-authenticate
                </button>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">
          Checking your onboarding progress...
        </div>
      );
    }

    if (onboardingError) {
      const message = onboardingError instanceof Error ? onboardingError.message : 'Unable to confirm onboarding status.';
      return (
        <div className="mx-auto flex min-h-[60vh] w-full max-w-xl flex-col items-center justify-center gap-4 px-4 text-center">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="text-sm text-gray-300">Unable to confirm your onboarding status.</p>
            <p className="mt-2 text-xs text-gray-400">{message}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  void refetchOnboardingStatus();
                }}
                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs text-white hover:bg-white/10"
              >
                Retry check
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/candidate/onboarding';
                }}
                className="rounded-lg border border-[#d2ac47]/60 bg-[#ba9036]/20 px-3 py-1.5 text-xs text-[#f3d98a] hover:bg-[#ba9036]/30"
              >
                Open onboarding
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!onboardingStatus?.onboardingCompleted) {
      return <Navigate to="/candidate/onboarding" state={{ returnTo: returnPath }} replace />;
    }

    if (requireTeamGate) {
      if (teamLoading || teamFetching) {
        return (
          <div className="flex min-h-[50vh] items-center justify-center text-sm text-gray-400">
            Verifying team dashboard access...
          </div>
        );
      }

      if (!teamSnapshot?.isTeamCandidate) {
        return <Navigate to="/candidate/dashboard" replace />;
      }
    }
  }

  return <>{children}</>;
}
